'use client';

import { useState } from 'react';
import { completeOnboardingAction, OnboardingData } from './onboarding-actions';
import PopupTemplate from '@/app/ui/popupTemplate';
import { useDevice } from '@/app/lib/contexts/DeviceContext';
import { useTranslations } from 'next-intl';
import { useLocale } from '@/app/user-and-session/languages/i18n';

interface OnboardingPopupProps {
  isOpen: boolean;
  onComplete: () => void;
  defaultPreferredName?: string;
}

export default function OnboardingPopup({ 
  isOpen, 
  onComplete, 
  defaultPreferredName = '' 
}: OnboardingPopupProps) {
  const t = useTranslations('popup.onboarding');
  const { deviceType } = useDevice();
  const { locale } = useLocale();
  const [preferredName, setPreferredName] = useState(defaultPreferredName);
  const [agreedToAge, setAgreedToAge] = useState(false);
  const [agreedToTos, setAgreedToTos] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setError(null);
    
    if (!preferredName.trim()) {
      setError(t('pleaseEnterPreferredName'));
      return;
    }

    if (!agreedToAge) {
      setError(t('mustConfirmAge18'));
      return;
    }

    if (!agreedToTos) {
      setError(t('mustAgreeToTerms'));
      return;
    }

    setIsSubmitting(true);
    try {
      const onboardingData: OnboardingData = {
        preferredName: preferredName.trim(),
        agreedToAge,
        agreedToTos,
        deviceType: deviceType || 'desktop', // Default to desktop if deviceType is null
        language: locale || null // Pass current locale from locale-provider
      };

      const result = await completeOnboardingAction(onboardingData);
      
      if (result.success) {
        onComplete();
      } else {
        setError(result.message);
      }
    } catch (error) {
      console.error('Onboarding failed:', error);
      setError(t('unexpectedError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const canProceed = preferredName.trim() && agreedToAge && agreedToTos && !isSubmitting;

  return (
    <PopupTemplate
      isOpen={isOpen}
      closeable={false}
      isTransparent={true}
    > 
      <div className="p-8">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-white mb-3">{t('oneLastStep')}</h1>
        </div>

        {/* Onboarding form */}
        <div className="space-y-4">
          {/* Preferred Name Input */}
          <div>
            <div className="bg-white/10 backdrop-blur-md border border-white/30 rounded-3xl focus-within:border-white/50 transition-colors">
              <input
                type="text"
                value={preferredName}
                onChange={(e) => setPreferredName(e.target.value)}
                className="w-full px-3 py-2 bg-transparent border-none text-white placeholder-gray-400 focus:outline-none transition-colors duration-200"
                placeholder={t('enterPreferredName')}
                disabled={isSubmitting}
              />
            </div>
            <p className="text-xs text-gray-300 mt-2 mb-2">
                {t('displayNameDescription')}
            </p>
          </div>

          {/* Agreement Checkboxes */}
          <div className="space-y-3">
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={agreedToAge}
                onChange={(e) => setAgreedToAge(e.target.checked)}
                disabled={isSubmitting}
                className="mt-1 h-4 w-4 text-[#5856d6] rounded border-white/30 bg-white/10 focus:ring-[#5856d6] disabled:opacity-50"
              />
              <span className="text-sm text-gray-300">
                {t('confirmAge18')}
              </span>
            </label>

            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={agreedToTos}
                onChange={(e) => setAgreedToTos(e.target.checked)}
                disabled={isSubmitting}
                className="mt-1 h-4 w-4 text-[#5856d6] rounded border-white/30 bg-white/10 focus:ring-[#5856d6] disabled:opacity-50"
              />
              <span className="text-sm text-gray-300">
                  {t('agreeToTerms')}{' '}
                  <a 
                    href="/terms-of-service" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-white hover:underline"
                  >
                    {t('termsOfService')}
                  </a>
                  {' '}{t('and')}{' '}
                  <a 
                    href="/privacy" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-white hover:underline"
                  >
                    {t('privacyPolicy')}
                  </a>
                </span>
            </label>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-900/50 border border-red-700 rounded-lg">
              <p className="text-red-300 text-sm">{error}</p>
            </div>
          )}

          {/* Submit Button */}
          <button
            onClick={handleSubmit}
            disabled={!canProceed}
            className="w-full bg-[#5856d6] hover:bg-[#5856d6] disabled:bg-gray-600/60 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-xl transition-all duration-200 transform hover:scale-[1.02] hover:shadow-lg disabled:transform-none disabled:shadow-none"
          >
            {isSubmitting ? (
              <div className="flex items-center justify-center space-x-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>{t('creatingAccount')}</span>
              </div>
            ) : (
              t('unlockUnlimitedChats')
            )}
          </button>
        </div>
      </div>
    </PopupTemplate>
  );
}
